/**
 * MCP OAuth 2.1 Service
 * Handles OAuth URL parsing and parameter validation
 */
(function () {
    'use strict';

    angular
        .module('app.core')
        .factory('mcpService', mcpService);

    mcpService.$inject = ['$window', '$rootScope', '$q', '$timeout', 'ngDialog', 'notifierSvc', 'oauth_factory', 'auth_factory', '$location'];

    function mcpService($window, $rootScope, $q, $timeout, ngDialog, notifierSvc, oauth_factory, auth_factory, $location) {
        var service = {
            mcpState: false, // MCP OAuth flow state flag
            emlakjetState: false, // Emlakjet OAuth flow state flag
            checkMcpFromResourceUrl: checkMcpFromResourceUrl,
            parseOAuthParams: parseOAuthParams,
            initializeOAuthParams: initializeOAuthParams,
            saveOAuthParams: saveOAuthParams,
            restoreOAuthParams: restoreOAuthParams,
            clearOAuthParams: clearOAuthParams,
            checkConsent: checkConsent,
            getConsentInfo: getConsentInfo,
            approve: approve,
            deny: deny,
            redirectToCallback: redirectToCallback,
            handleOAuthConsentFlow: handleOAuthConsentFlow,
            checkEmlakjetCallback: checkEmlakjetCallback,
            handleEmlakjetCallback: handleEmlakjetCallback
        };

        return service;

        // ====================================================================

        /**
         * Check resource parameter from URL and set MCP state
         * @returns {boolean} True if MCP OAuth request, false otherwise
         */
        function checkMcpFromResourceUrl() {
            // If already set to true, don't change it (persist OAuth flow)
            if (service.mcpState === true) {
                return true;
            }

            var resource = $window.getUrlParameter('resource', true);

            if (resource) {
                // Check if resource contains 'mcp' (emlakjet temporarily disabled)
                var hasMcp = resource.toLowerCase().indexOf('mcp') !== -1;

                if (hasMcp) {
                    service.mcpState = true;
                    return true;
                } else {
                    service.mcpState = false;
                    return false;
                }
            }
        }

        /**
         * Parse all OAuth parameters from URL
         * @returns {Object} OAuth parameters
         */
        function parseOAuthParams() {

            var params = {
                response_type: $window.getUrlParameter('response_type', true),
                client_id: $window.getUrlParameter('client_id', true),
                redirect_uri: $window.getUrlParameter('redirect_uri', true),
                state: $window.getUrlParameter('state', true),
                scope: $window.getUrlParameter('scope', true),
                code_challenge: $window.getUrlParameter('code_challenge', true),
                code_challenge_method: $window.getUrlParameter('code_challenge_method', true),
                prompt: $window.getUrlParameter('prompt', true),
                resource: $window.getUrlParameter('resource', true)
            };

            // Clean up - remove null/undefined values
            for (var key in params) {
                if (!params[key]) {
                    delete params[key];
                }
            }

            return params;
        }

        /**
         * Initialize OAuth parameters - handles parsing, saving, and restoring logic
         * @returns {Object} OAuth parameters or null
         */
        function initializeOAuthParams() {
            var oauthParams = null;

            if (service.mcpState) {
                // MCP is active, parse and save OAuth params from URL
                oauthParams = parseOAuthParams();
                if (oauthParams && Object.keys(oauthParams).length > 0) {
                    saveOAuthParams(oauthParams);
                }
            } else {
                // Try to restore OAuth params from session storage
                oauthParams = restoreOAuthParams();
                if (oauthParams) {
                    service.mcpState = true;
                }
            }

            return oauthParams;
        }

        function saveOAuthParams(params) {
            if (params && Object.keys(params).length > 0) {
                $window.sessionStorage.setItem('mcp_oauth_params', JSON.stringify(params));
            }
        }

        function restoreOAuthParams() {
            var savedParams = $window.sessionStorage.getItem('mcp_oauth_params');
            if (savedParams) {
                try {
                    return JSON.parse(savedParams);
                } catch (e) {
                    console.error('[mcpService] Error parsing saved OAuth params:', e);
                    return null;
                }
            }
            return null;
        }

        function clearOAuthParams() {
            $window.sessionStorage.removeItem('mcp_oauth_params');
            service.mcpState = false; // Reset MCP state when clearing OAuth params
        }

        /**
         * Check if user has already given consent for this client
         * @param {Object} oauthParams - OAuth parameters
         * @returns {Promise} Check consent response with hasConsent and approvalData
         */
        function checkConsent(oauthParams) {
            var deferred = $q.defer();

            if (!$rootScope.SignedIn) {
                console.error('[mcpService] User not signed in');
                deferred.reject({ error: 'login_required', error_description: 'User not authenticated' });
                return deferred.promise;
            }

            oauth_factory.checkConsent(oauthParams).$promise.then(
                function (response) {
                    deferred.resolve(response);
                },
                function (error) {
                    console.error('[mcpService] Error checking consent:', error);
                    deferred.reject(error.data || { error: 'server_error' });
                }
            );

            return deferred.promise;
        }

        /**
         * Get consent info from backend
         * @param {Object} oauthParams - OAuth parameters
         * @returns {Promise} Consent data
         */
        function getConsentInfo(oauthParams) {
            var deferred = $q.defer();

            if (!$rootScope.SignedIn) {
                console.error('[mcpService] User not signed in');
                deferred.reject({ error: 'login_required', error_description: 'User not authenticated' });
                return deferred.promise;
            }

            oauth_factory.oauthAuthorize(oauthParams).$promise.then(
                function (response) {
                    deferred.resolve(response);
                },
                function (error) {
                    console.error('[mcpService] Error getting consent:', error);
                    deferred.reject(error.data || { error: 'server_error' });
                }
            );

            return deferred.promise;
        }

        function approve(oauthParams) {
            var deferred = $q.defer();

            if (!$rootScope.SignedIn) {
                console.error('[mcpService] User not signed in');
                deferred.reject({ error: 'login_required' });
                return deferred.promise;
            }

            oauth_factory.oauthApprove(oauthParams).$promise.then(
                function (response) {
                    deferred.resolve(response);
                },
                function (error) {
                    console.error('[mcpService] Error approving:', error);
                    deferred.reject(error.data || { error: 'server_error' });
                }
            );

            return deferred.promise;
        }

        function deny(oauthParams) {
            var deferred = $q.defer();

            if (!$rootScope.SignedIn) {
                console.error('[mcpService] User not signed in');
                deferred.reject({ error: 'login_required' });
                return deferred.promise;
            }

            oauth_factory.oauthDeny(oauthParams).$promise.then(
                function (response) {
                    deferred.resolve(response);
                },
                function (error) {
                    console.error('[mcpService] Error denying:', error);
                    deferred.reject(error.data || { error: 'server_error' });
                }
            );

            return deferred.promise;
        }

        function redirectToCallback(response) {
            if (!response || !response.redirect_uri) {
                console.error('[mcpService] Invalid redirect response:', response);
                return;
            }

            // Clear OAuth params and reset MCP state before redirect
            clearOAuthParams();

            var redirectUri = response.redirect_uri;
            var params = [];

            // Success: authorization code
            if (response.code) {
                params.push('code=' + encodeURIComponent(response.code));
                if (response.state) {
                    params.push('state=' + encodeURIComponent(response.state));
                }
            }
            // Error: access denied or other error
            else if (response.error) {
                params.push('error=' + encodeURIComponent(response.error));
                if (response.error_description) {
                    params.push('error_description=' + encodeURIComponent(response.error_description));
                }
                if (response.state) {
                    params.push('state=' + encodeURIComponent(response.state));
                }
            }

            var separator = redirectUri.indexOf('?') > -1 ? '&' : '?';
            var finalUrl = redirectUri + separator + params.join('&');

            $window.location.href = finalUrl;
        }

        /**
         * Centralized OAuth consent flow handler
         * @param {Object} oauthParams - OAuth parameters
         * @param {boolean} closeExistingModals - Whether to close existing modals before opening consent modal
         * @returns {Promise} Promise that resolves when flow is complete
         */
        function handleOAuthConsentFlow(oauthParams, closeExistingModals) {
            var deferred = $q.defer();

            if (!oauthParams || !Object.keys(oauthParams).length) {
                console.error('[mcpService] No OAuth params provided');
                deferred.reject({ error: 'invalid_request', error_description: 'No OAuth parameters' });
                return deferred.promise;
            }

            // Close existing modals if requested
            if (closeExistingModals) {
                ngDialog.close();

                // Wait for modal close animation to complete before making API call
                $timeout(function() {
                    proceedWithOAuthFlow();
                }, 300);
            } else {
                proceedWithOAuthFlow();
            }

            function proceedWithOAuthFlow() {
                // First get consent information (client details, scopes, etc.)
                service.getConsentInfo(oauthParams).then(
                    function (consentData) {
                        // Now check if user has already given consent for this client
                        service.checkConsent(oauthParams).then(
                            function (checkResponse) {
                                // If user has already consented, redirect directly with approval data
                                if (checkResponse.hasConsent && checkResponse.approvalData) {
                                    service.redirectToCallback(checkResponse.approvalData);
                                    deferred.resolve(checkResponse.approvalData);
                                    return;
                                }

                                // User hasn't consented yet, show consent modal
                                openConsentModal(consentData);
                            },
                            function (error) {
                                console.error('[mcpService] Error checking consent, proceeding with consent modal:', error);
                                // If check fails, proceed with normal consent flow
                                openConsentModal(consentData);
                            }
                        );
                    },
                    function (error) {
                        console.error('[mcpService] Error getting consent info:', error);

                        // Redirect with error if redirect_uri exists
                        if (error.error && oauthParams.redirect_uri) {
                            service.redirectToCallback({
                                redirect_uri: oauthParams.redirect_uri,
                                error: error.error,
                                error_description: error.error_description || error.message,
                                state: oauthParams.state
                            });
                        }

                        deferred.reject(error);
                    }
                );
            }

            function openConsentModal(consentData) {
                // Open consent modal with centralized scope
                var dialog = ngDialog.open({
                    template: 'app/security/auth-signin/oauth-consent.modal.html',
                    closeByDocument: false,
                    closeByEscape: false,
                    controller: ['$scope', '$rootScope', '$window', '$timeout', '$state', function($scope, $rootScope, $window, $timeout, $state) {
                        $scope.oauthConsent = consentData;
                        $scope.oauthLoading = false;
                        $scope.oauthError = null;

                        // Approve handler
                        $scope.approveOAuth = function () {
                            $scope.oauthLoading = true;
                            service.approve(oauthParams).then(
                                function (response) {
                                    $scope.oauthLoading = false;
                                    ngDialog.close();
                                    service.redirectToCallback(response);
                                    deferred.resolve(response);
                                },
                                function (error) {
                                    console.error('[mcpService] Error approving:', error);
                                    $scope.oauthError = error;
                                    $scope.oauthLoading = false;

                                    // Redirect with error even if approve fails
                                    service.redirectToCallback({
                                        redirect_uri: oauthParams.redirect_uri,
                                        error: 'server_error',
                                        error_description: error.error_description || 'Authorization failed',
                                        state: oauthParams.state
                                    });

                                    deferred.reject(error);
                                }
                            );
                        };

                        // Deny handler
                        $scope.denyOAuth = function () {
                            $scope.oauthLoading = true;
                            service.deny(oauthParams).then(
                                function (response) {
                                    $scope.oauthLoading = false;
                                    ngDialog.close();
                                    service.redirectToCallback(response);
                                    deferred.resolve(response);
                                },
                                function (error) {
                                    console.error('[mcpService] Error denying:', error);
                                    $scope.oauthLoading = false;

                                    // Redirect with error even if deny fails
                                    service.redirectToCallback({
                                        redirect_uri: oauthParams.redirect_uri,
                                        error: 'access_denied',
                                        error_description: 'User denied authorization',
                                        state: oauthParams.state
                                    });

                                    deferred.reject(error);
                                }
                            );
                        };

                        // Terms modal handler
                        $scope.termsModal = function () {
                            ngDialog.open({
                                templateUrl: 'app/content/sozlesme/' + window.lang + '/kullanim.html',
                                controller: ['$scope', function($scope) {
                                    $scope.onModal = true;
                                }]
                            });
                        };

                        // Privacy modal handler
                        $scope.gizlilikModal = function () {
                            ngDialog.open({
                                templateUrl: 'app/content/sozlesme/' + window.lang + '/gizlilik.html',
                                controller: ['$scope', function($scope) {
                                    $scope.onModal = true;
                                }]
                            });
                        };

                        // Switch account handler
                        $scope.switchAccount = function () {
                            // Close modal and sign out
                            ngDialog.close();

                            // Sign out
                            $rootScope.signOut();

                            // Wait for signOut to complete, then redirect to signin
                            $timeout(function() {
                                var signInUrl = '/' + $rootScope.lang + '/signin';
                                $window.location.href = signInUrl;
                            }, 500);
                        };
                    }]
                });
            }

            return deferred.promise;
        }

        // ====================================================================
        // Emlakjet OAuth Callback Handling
        // ====================================================================

         /**
         * Check if current URL contains Emlakjet OAuth callback parameters
         * @returns {Object|null} Emlakjet callback params or null
         */
        function checkEmlakjetCallback() {
            var code = $window.getUrlParameter('code', true);
            var state = $window.getUrlParameter('state', true);
            var resource = $window.getUrlParameter('resource', true);

            var isEmlakjetCallback = resource && resource.indexOf('emlakjet.com') !== -1;

            if (!isEmlakjetCallback) {
                return null;
            }

            // Return params even if incomplete - let handleEmlakjetCallback show error
            return {
                code: code,
                state: state,
                hasError: !code || !state
            };
        }


        /**
         * Handle Emlakjet OAuth callback
         * @param {Object} params - Callback parameters (code, state)
         * @returns {Promise} Promise that resolves with callback result
         */
        function handleEmlakjetCallback(params) {
            var deferred = $q.defer();

            if (!params || !params.code || !params.state) {
                deferred.reject({ error: 'invalid_request', error_description: 'Missing code or state parameter' });
                return deferred.promise;
            }

            service.emlakjetState = true;

            auth_factory.emlakjetCallback({ code: params.code, state: params.state }).$promise.then(
                function (result) {
                    service.emlakjetState = false;
                    clearEmlakjetUrlParams();
                    deferred.resolve(result);
                },
                function (error) {
                    service.emlakjetState = false;
                    clearEmlakjetUrlParams();
                    console.error('[mcpService] Emlakjet callback error:', error);
                    deferred.reject(error);
                }
            );

            return deferred.promise;
        }

        /**
         * Clear Emlakjet callback parameters from URL
         */
        function clearEmlakjetUrlParams() {
            var url = new URL($window.location.href);
            url.searchParams.delete('code');
            url.searchParams.delete('state');
            url.searchParams.delete('resource');
            $window.history.replaceState({}, document.title, url.toString());
        }

    }
})();
